import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé avec succès !");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="contact-page">
      {/* Hero with Maud - Full width introduction */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[500px]">
          {/* Left: Maud's Photo */}
          <div className="relative bg-[#1D3557] overflow-hidden hidden lg:block">
            <div className="absolute inset-0">
              <img
                src="https://customer-assets.emergentagent.com/job_ee3310b8-7809-406b-b5bc-7f55e062e19b/artifacts/4bb1xx0j_%40PARLACOM-MAUD-35.jpg"
                alt="Maud Dupupet - Fondatrice MOREATI"
                className="w-full h-full object-cover object-center"
                data-testid="maud-hero-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1D3557]/30" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1D3557]/80 to-transparent" />
            </div>
            <div className="absolute bottom-8 left-8 right-8 z-10">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <h3 className="text-white font-bold text-xl mb-1" style={{ fontFamily: "Fraunces, serif" }}>
                  Maud Dupupet
                </h3>
                <p className="text-white/80 text-sm">Fondatrice — Dirigeante</p>
              </div>
            </div>
          </div>

          {/* Right: Text Content */}
          <div className="bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] flex items-center py-16 lg:py-0">
            <div className="px-8 md:px-12 lg:px-16 max-w-xl mx-auto lg:mx-0 w-full">
              {/* Mobile photo */}
              <div className="lg:hidden mb-8 flex justify-center">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#8C9D78] shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_ee3310b8-7809-406b-b5bc-7f55e062e19b/artifacts/4bb1xx0j_%40PARLACOM-MAUD-35.jpg"
                    alt="Maud Dupupet"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 mb-6">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Contactez-nous</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Parlons de votre{" "}
                <span className="text-[#8C9D78]">projet</span>
              </h1>
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                Une question ? Un projet ?
                N'hésitez pas à me contacter, je vous répondrai dans les meilleurs délais.
              </p>
              <div className="space-y-4">
                <a href="tel:+33751131092" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors group">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#8C9D78] transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/50">Téléphone</p>
                    <p className="font-medium">07 51 13 10 92</p>
                  </div>
                </a>
                <a href="mailto:maud@moreati.fr" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors group">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#C65D47] transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/50">Email</p>
                    <p className="font-medium">maud@moreati.fr</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-custom py-20">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-[#1D3557] mb-6" style={{ fontFamily: "Fraunces, serif" }}>
                Coordonnées
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C65D47]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#C65D47]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1D3557]">Adresse</h4>
                    <p className="text-stone-600">Siège : 4 avenue du Mont Thabor</p>
                    <p className="text-stone-600">20090 Ajaccio, Corse</p>
                    <p className="text-stone-500 text-sm mt-2">Bureau : Cité Grossetti - Bd Albert 1er, 20000 Ajaccio</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#8C9D78]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#8C9D78]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1D3557]">Téléphone</h4>
                    <a href="tel:+33751131092" className="text-stone-600 hover:text-[#C65D47]">
                      07 51 13 10 92
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1D3557]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#1D3557]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1D3557]">Email</h4>
                    <a href="mailto:maud@moreati.fr" className="text-stone-600 hover:text-[#C65D47]">
                      maud@moreati.fr
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C65D47]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#C65D47]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1D3557]">Horaires</h4>
                    <p className="text-stone-600">Lun - Ven : 9h00 - 18h00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="font-semibold text-[#1D3557] mb-4">Suivez-nous</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/maud_agence_moreati"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-[#C65D47] hover:text-white transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/agencemoreati/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-[#1D3557] hover:text-white transition-colors"
                  data-testid="linkedin-link"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-64 bg-stone-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3015.8867831736424!2d8.7346!3d41.9256!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12da89ee2e2e2e2e%3A0x2e2e2e2e2e2e2e2e!2sAjaccio%2C%20France!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <h2 className="text-2xl font-bold text-[#1D3557] mb-2" style={{ fontFamily: "Fraunces, serif" }}>
                Envoyez-moi un message
              </h2>
              <p className="text-stone-600 mb-8">
                Remplissez le formulaire ci-dessous et nous vous recontacterons rapidement.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Votre nom"
                      className="mt-2"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="mt-2"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Sujet *</Label>
                  <Select
                    value={form.subject}
                    onValueChange={(v) => setForm({ ...form, subject: v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="contact-subject-select">
                      <SelectValue placeholder="Sélectionnez un sujet" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Piloter sa stratégie">Piloter sa stratégie</SelectItem>
                      <SelectItem value="Développer ses ventes">Développer ses ventes</SelectItem>
                      <SelectItem value="Fidéliser ses clients">Fidéliser ses clients</SelectItem>
                      <SelectItem value="Valoriser sa marque">Valoriser sa marque</SelectItem>
                      <SelectItem value="Formation / Sensibilisation">Formation / Sensibilisation</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Décrivez votre projet ou votre demande..."
                    className="mt-2 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-14 text-lg font-semibold"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    "Envoi en cours..."
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
